// ─── Angular Imports ───────────────────────────────────────────────────────────
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// ─── Third-Party Imports ───────────────────────────────────────────────────────
import { Subject, takeUntil } from 'rxjs';

// ─── Application Imports ───────────────────────────────────────────────────────
import { BatchesService } from 'src/app/services/batches.services';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { IBatch } from 'src/app/models/batches/batch';
import { BatchResult } from 'src/app/models/batches/batch-result';
import { OrganizationResult } from 'src/app/models/organizations/organization-result';

@Component({
    selector: 'app-batch-details',
    templateUrl: './batch-details.component.html',
    styleUrls: ['./batch-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class BatchDetailsComponent implements OnInit, OnDestroy {
  // ─── State ───────────────────────────────────────────────────────────────────
  currentBatch!: IBatch;
  errorState: boolean = false;
  errorMessages: string[] = [];
  serverErrorMessage: string = '';
  loaded: boolean = false;

  // ─── Observables ─────────────────────────────────────────────────────────────
  private destroy$ = new Subject<void>();

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private batchesService: BatchesService,
    private router: Router,
    private route: ActivatedRoute,
    private organizationsService: OrganizationsService,
  ) {}

  // ─── Lifecycle Hooks ────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadBatchDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Data Fetching Methods ──────────────────────────────────────────────────
  private loadBatchDetails(): void {
    const batch = this.batchesService.getCurrentBatch();
    const batchId = this.route.snapshot.paramMap.get('batchId');

    if (!batch && batchId) {
      this.batchesService
        .detailsBatch(batchId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: BatchResult) => {
          this.currentBatch = result.batch;
          this.loaded = true;
        });
    } else {
      this.loaded = true;
      this.currentBatch = batch;
    }
  }

  // ─── Action Methods ─────────────────────────────────────────────────────────
  manageBatch(batch: IBatch): void {
    this.batchesService.setCurrentBatch(batch);

    this.organizationsService
      .getOrganization(batch.organizationId!)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: OrganizationResult) => {
        this.organizationsService.setCurrentOrg(result.organization);
      });

    this.router.navigateByUrl(`batches/${batch.id}/products`);
  }
}
